<script setup lang="ts">
import PackingListForm from '@/components/features/packingList/PackingListForm.vue'
import { useRouter, RouterLink } from 'vue-router'
import { Icon } from '@iconify/vue'
import Button from 'primevue/button'
import { usePackingListDetailsForm } from '@/composables/usePackingListForm'
import StatusTag from '@/components/table/StatusTag.vue'
import {computed, onMounted, ref, watchEffect} from 'vue'
import PackingListDialogue from '@/components/features/packingList/PackingListDialogue.vue'
import InvoiceDialog from '@/components/features/packingList/InvoiceDialog.vue'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import { getOrders } from '@/api/orders'
import { getBatchById, getBatchList } from "@/api/batches";
import { useToast } from 'primevue/usetoast'
import {deliverPackingList, dispatchPackingList} from "@/api/packingList";
import {PackingListState} from "@/types/packingList-types";
import type {Batch, BatchRoll} from "@/types/batch-types";
import { getRollById } from "@/api/rolls";
import { getRollState } from "@/constants/rolls";
import {formatedDate} from "@/constants";
import PackingListPdfDialog from '@/components/features/packingList/PackingListPdfDialog.vue'
import {getBatchRoutePath} from "@/utils/batch-navigation";

const toast = useToast()
const router = useRouter()
const { values, handleSubmit, setErrors, formType } = usePackingListDetailsForm('view')
const packingListId = router.currentRoute.value.params.id as string
const showDispatchModal = ref(false)
const showDeliveryModal = ref(false)
const showInvoiceModal = ref(false)
const showPackingListPdfModal = ref(false)
const orders = ref<any>([])
const batches = ref<any>([])
const packingListStatus = ref<PackingListState>(PackingListState.Pending)

watchEffect(() => {
  if (values.packingListState !== undefined) {
    packingListStatus.value = values.packingListState as PackingListState
  }
})

const handleDispatch = async (data: any) => {
  try {
    const payload = {
      dispatchDate: data.dispatchDate,
      enteredBy: data.enteredBy
    }
    await dispatchPackingList(packingListId, payload)
    showDispatchModal.value = false
    packingListStatus.value = PackingListState.Dispatched
    router.go(0)
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to dispatch packing list',
      life: 3000
    })
  }
}

const handleDelivery = async (data: any) => {
  try {
    const payload = {
      deliveryDate: data.deliveryDate,
      enteredBy: data.enteredBy
    }
    await deliverPackingList(packingListId, payload)
    showDeliveryModal.value = false
    packingListStatus.value = PackingListState.Delivered
    router.go(0)
  } catch (error) {
    console.error('Error delivering packing list:', error)
  }
}

const handlePackingListPdfClose = () => {
  showPackingListPdfModal.value = false
}

const getOrderOrBatchName = (id: string | null, type: 'order' | 'batch') => {
  if (!id) {
    return type === 'order' ? 'Order Not Found' : 'Batch Not Found';
  }

  if (type === 'order') {
    const order = orders.value.find((order: any) => order.id === id);
    return order ? order.orderNumber : 'Order Not Found';
  }

  if (type === 'batch') {
    const batch = batches.value.find((batch: any) => batch.id === id);
    return batch ? batch.batchNumber : 'Batch Not Found';
  }

  return 'Invalid Type';
};

const getStatusColor = computed(() => {
  switch (packingListStatus.value) {
    case PackingListState.Dispatched:
      return 'warning'
    case PackingListState.Delivered:
      return 'success'
    default:
      return 'info'
  }
})

const statusText = computed(() => {
  switch (packingListStatus.value) {
    case PackingListState.Dispatched:
      return 'Dispatched'
    case PackingListState.Delivered:
      return 'Delivered'
    default:
      return 'Pending'
  }
})

const stockList = ref<BatchRoll[]>([])

const isLoading = ref(false);
const fetchRollsData = async () => {
  if (values.rollIds && values.rollIds.length > 0) {
    try {
      isLoading.value = true
      const rollPromises = values.rollIds.map(async (id: string) => {
        const roll = await getRollById(id)
        if (roll.batchId) {
          const batch = await getBatchById(roll.batchId)
          return {
            ...roll,
            batchNumber: batch.batchNumber
          }
        }
        return roll
      })
      stockList.value = await Promise.all(rollPromises)
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch roll details',
        life: 3000
      })
    } finally {
      isLoading.value = false
    }
  }
}

const formatDate = (date: string) => {
  return formatedDate(date)
}

const dispatchButtonText = computed(() => {
  return values.packingListType === 0 ? 'Dispatch to Supplier' : 'Dispatch to Customer'
})

const deliveryButtonText = computed(() => {
  return values.packingListType === 0 ? 'Deliver to Supplier' : 'Deliver to Customer'
})

watchEffect(() => {
  if (values.rollIds && values.rollIds.length > 0) {
    fetchRollsData();
  }
})

const getBatchRoute = (batchId: string) => {
  const batch = batches.value.find((batch: Batch) => batch.id === batchId);
  return getBatchRoutePath(batch);
}

onMounted(async () => {
  packingListStatus.value = values.packingListState as PackingListState
  orders.value = await getOrders()
  batches.value = await getBatchList()
  await fetchRollsData()
})
</script>

<template>
  <div class="flex justify-between items-center mb-10">
    <RouterLink
        to="/packingList"
        class="flex items-center gap-x-2 group hover:text-primary-default"
    >
      <Icon
          icon="material-symbols:arrow-back-rounded"
          class="text-3xl group-hover:translate-x-2 transition-transform"
      />
      <span>Back</span>
    </RouterLink>

    <h1 class="font-semibold text-2xl">
      {{ values.packingListType === 0 ? 'Supplier Packing List' : 'Customer Packing List' }}
    </h1>

    <div class="flex flex-row justify-between items-center gap-x-4">
      <Button
          v-if="values.packingListType !== undefined && values.packingListType !== 0"
          outlined
          class="flex items-center gap-x-2"
          @click="showInvoiceModal = true"
      >
        <span> View Invoice </span>
      </Button>
      <Button label="View PDF" @click="showPackingListPdfModal = true" />
    </div>
  </div>

  <div class="flex justify-between items-center mb-10">
    <div>
      <div class="flex flex-row justify-between items-center gap-x-4">
        <div
            class="bg-opacity-25 py-3 px-5 pe-20 rounded-lg bg-primary-300 border border-primary-500 text-primary-500'"
        >
          <p class="text-primary-500">Packing List Number: {{ values?.packingListId }}</p>
        </div>
        <StatusTag :status="statusText" :color="getStatusColor" />
      </div>
      <p class="text-neutral-500 text-sm mt-2">
        <span v-if="values?.orderId">
          Order Number:
          <RouterLink
              :to="`/orders/${values.orderId}`"
              class="font-medium text-neutral-800 hover:underline"
          >
            {{ getOrderOrBatchName(values?.orderId, 'order') }}
          </RouterLink>
          <span class="text-primary-500 font-semibold"> ></span>
        </span>
      </p>

      <p class="text-neutral-500 text-sm mt-2">
        <span v-if="values?.batchId">
          Batch Number:
          <RouterLink
              v-if="values?.batchId"
              :to="getBatchRoute(values?.batchId)"
              class="font-medium text-neutral-800 hover:underline"
          >
              {{ getOrderOrBatchName(values?.batchId, 'batch') }}
            </RouterLink>
          <span class="text-primary-500 font-semibold"> ></span>
        </span>
      </p>
    </div>
    <div class="flex flex-row justify-between items-center gap-x-4">
      <Button
          outlined
          class="flex items-center gap-x-2"
          @click="
          () => {
            router.push(`/packingList/${packingListId}/edit`)
          }
        "
      >
        <span> Edit Packing List </span>
        <Icon icon="basil:edit-solid" class="text-xl ml-3 order-1" />
      </Button>
      <Button
          :label="dispatchButtonText"
          @click="() => { showDispatchModal = true }"
          v-if="packingListStatus === PackingListState.Pending"
      >
        <template #icon>
          <Icon icon="mdi:truck-fast" class="text-xl ml-3 order-1" />
        </template>
      </Button>
      <Button
          :label="deliveryButtonText"
          @click="() => { showDeliveryModal = true }"
          v-if="packingListStatus === PackingListState.Dispatched"
      >
        <template #icon>
          <Icon icon="mdi:package-check" class="text-xl ml-3 order-1" />
        </template>
      </Button>
    </div>
  </div>

  <InvoiceDialog
      :visible="showInvoiceModal"
      :onClose="() => showInvoiceModal = false"
      :packingListId="values?.packingListId"
  />

  <PackingListPdfDialog
      :visible="showPackingListPdfModal"
      :onClose="handlePackingListPdfClose"
      :packingListId="packingListId"
  />

  <PackingListDialogue
      :visible="showDispatchModal"
      @update:visible="val => showDispatchModal = val"
      :packingListNumber="values?.packingListNumber"
      actionStatus="Dispatched"
      confirmLabel="Dispatch Packing List"
      :enteredBy="values.lastEditedBy"
      @confirm="handleDispatch"
  />
  <PackingListDialogue
      :visible="showDeliveryModal"
      @update:visible="val => showDeliveryModal = val"
      :packingListNumber="values?.packingListNumber"
      actionStatus="Delivered"
      confirmLabel="Mark as Delivered"
      :enteredBy="values.lastEditedBy"
      @confirm="handleDelivery"
  />
  <div class="bg-white rounded-xl pb-2">
    <PackingListForm
        :values="values"
        :type="formType"
        @submit="handleSubmit"
        :setErrors="setErrors"
    />

    <div class="p-6">
      <h1 class="font-semibold text-2xl">Stock</h1>
      <DataTable
          :value="stockList"
          paginator
          :rows="10"
          tableStyle="min-width: 50rem"
          stripedRows
          paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
          :rowsPerPageOptions="[10, 25]"
          :pt-options="{
      mergeProps: true
    }"
          class="w-full"
      >
        <Column field="rollNumber" header="Roll Number">
          <template #body="slotProps">
            <RouterLink
                :to="`/rolls/${slotProps.data.id}`"
                class="font-medium text-neutral-800 block hover:underline"
            >
              {{ slotProps.data.rollNumber }}
            </RouterLink>
          </template>
        </Column>
        <Column field="weight" header="Weight" />
        <Column field="lengthValue" header="Length" />
        <Column field="batchNumber" header="Batch Number">
          <template #body="slotProps">
            <RouterLink
                v-if="slotProps.data.batchId"
                :to="getBatchRoute(slotProps.data.batchId)"
                class="font-medium text-neutral-800 hover:underline"
            >
              {{ slotProps.data.batchNumber }}
            </RouterLink>
            <span v-else>{{ slotProps.data.batchNumber }}</span>
          </template>
        </Column>
        <Column field="createdAt" header="Date Knitted">
          <template #body="slotProps">
            {{ formatDate(slotProps.data.dateKnitted) }}
          </template>
        </Column>
        <Column field="rollState" header="State">
          <template #body="slotProps">
            {{ getRollState(slotProps.data.rollState) }}
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>